import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import { useSessionStorage } from 'react-use';

import { ErrorLabel } from '../../../components';
import {
    Checkbox,
    IconButton,
    PasswordInput,
    RecaptchaDisclaimer,
    TextInput,
} from '../../../compositions';
import { Customer } from '../../../entities/@account/Customer/Customer';
import { CheckoutDetailsFormAttribute, CheckoutDetailsFormData } from '../../../entities/@forms/Checkout/Checkout';
import { FormProps } from '../../../entities/@forms/Form/Form';
import { AddressType } from '../../../entities/Address/Address';
import { HorizontalAlignment } from '../../../entities/Alignment/Alignment';
import { LinkTarget } from '../../../entities/Link/Link';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { scrollIntoViewIncludingNavigation } from '../../../helpers/scroll';
import { useTrans } from '../../../hooks';
import { CheckoutDetailsFormErrors, validateCheckoutDetailsFormData } from './validations';

import './CheckoutDetailsForm.scss';

interface CheckoutDetailsFormProps extends FormProps<CheckoutDetailsFormData> {
    customer?: Customer;
    className?: string;
}

export const CheckoutDetailsForm: FC<CheckoutDetailsFormProps> = ({
    isLoading,
    customer,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formRef = useRef<HTMLFormElement>(null);
    const firstNameInputRef = useRef<HTMLInputElement>(null);

    const [formErrors, setFormErrors] = useState<CheckoutDetailsFormErrors>({});
    const [botDetected, setBotDetected] = useState<boolean>(false);

    const [firstName, storeFirstName] = useSessionStorage<string>(CheckoutDetailsFormAttribute.firstName, '');
    const [lastName, storeLastName] = useSessionStorage<string>(CheckoutDetailsFormAttribute.lastName, '');
    const [email, storeEmail] = useSessionStorage<string>(CheckoutDetailsFormAttribute.email, '');
    const [phoneNumber, storePhoneNumber] = useSessionStorage<string>(CheckoutDetailsFormAttribute.phoneNumber, '');
    const [subscribeToNewsletter, storeSubscribeToNewsletter] = useSessionStorage<boolean>(CheckoutDetailsFormAttribute.subscribeToNewsletter, false);

    const [shippingStreet, storeShippingStreet] = useSessionStorage<string>(CheckoutDetailsFormAttribute.shippingStreet, '');
    const [shippingHouseNumber, storeShippingHouseNumber] = useSessionStorage<string>(CheckoutDetailsFormAttribute.shippingHouseNumber, '');
    const [shippingAddition, storeShippingAddition] = useSessionStorage<string>(CheckoutDetailsFormAttribute.shippingAddition, '');
    const [shippingPostalCode, storeShippingPostalCode] = useSessionStorage<string>(CheckoutDetailsFormAttribute.shippingPostalCode, '');
    const [shippingCity, storeShippingCity] = useSessionStorage<string>(CheckoutDetailsFormAttribute.shippingCity, '');

    const [hasBillingAddress, storeHasBillingAddress] = useSessionStorage<boolean>(CheckoutDetailsFormAttribute.hasBillingAddress, false);

    const [billingStreet, storeBillingStreet] = useSessionStorage<string>(CheckoutDetailsFormAttribute.billingStreet, '');
    const [billingHouseNumber, storeBillingHouseNumber] = useSessionStorage<string>(CheckoutDetailsFormAttribute.billingHouseNumber, '');
    const [billingAddition, storeBillingAddition] = useSessionStorage<string>(CheckoutDetailsFormAttribute.billingAddition, '');
    const [billingPostalCode, storeBillingPostalCode] = useSessionStorage<string>(CheckoutDetailsFormAttribute.billingPostalCode, '');
    const [billingCity, storeBillingCity] = useSessionStorage<string>(CheckoutDetailsFormAttribute.billingCity, '');

    const [createAccount, storeCreateAccount] = useSessionStorage<boolean>(CheckoutDetailsFormAttribute.createAccount, false);
    const [password, setPassword] = useState<string>('');

    useEffect((): void => {
        if (firstNameInputRef.current) {
            firstNameInputRef.current.focus();
        }
    }, [firstNameInputRef]);

    // Fill in form with customer data, whenever customer is available
    useEffect((): void => {
        if (!customer) {
            return;
        }

        const shippingAddress = customer.addresses.find(address => address.type === AddressType.shipping);

        storeFirstName(customer.firstName);
        storeLastName(customer.lastName);
        storeEmail(customer.email);
        storePhoneNumber(customer.phoneNumber);

        if (shippingAddress) {
            storeShippingStreet(shippingAddress.street);
            storeShippingHouseNumber(shippingAddress.houseNumber);
            storeShippingAddition(shippingAddress.addition);
            storeShippingPostalCode(shippingAddress.postalCode);
            storeShippingCity(shippingAddress.city);

            storeBillingStreet(shippingAddress.street);
            storeBillingHouseNumber(shippingAddress.houseNumber);
            storeBillingAddition(shippingAddress.addition);
            storeBillingPostalCode(shippingAddress.postalCode);
            storeBillingCity(shippingAddress.city);
        }
    }, [customer]);

    // Fill in billing address information with customer's billing address preset
    useLayoutEffect((): void => {
        if (!customer) {
            return;
        }

        const shippingAddress = customer.addresses.find(address => address.type === AddressType.shipping);
        const customerBillingAddress = customer.addresses.find(address => address.type === AddressType.billing);
        const billingAddress = hasBillingAddress
            ? customerBillingAddress || shippingAddress
            : shippingAddress;

        if (billingAddress) {
            storeBillingStreet(billingAddress.street);
            storeBillingHouseNumber(billingAddress.houseNumber);
            storeBillingAddition(billingAddress.addition);
            storeBillingPostalCode(billingAddress.postalCode);
            storeBillingCity(billingAddress.city);
        }
    }, [customer, hasBillingAddress]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!executeRecaptcha) {
            setBotDetected(true);
            return;
        }

        const shippingAddress = {
            shippingStreet,
            shippingHouseNumber,
            shippingAddition,
            shippingPostalCode,
            shippingCity,
        };

        const billingAddress = {
            billingStreet: hasBillingAddress ? billingStreet : shippingStreet,
            billingHouseNumber: hasBillingAddress ? billingHouseNumber : shippingHouseNumber,
            billingAddition: hasBillingAddress ? billingAddition : shippingAddition,
            billingPostalCode: hasBillingAddress ? billingPostalCode : shippingPostalCode,
            billingCity: hasBillingAddress ? billingCity : shippingCity,
        };

        const formData: CheckoutDetailsFormData = {
            ...shippingAddress,
            ...billingAddress,
            firstName,
            lastName,
            email,
            phoneNumber,
            subscribeToNewsletter,
            hasBillingAddress,
            createAccount,
            password,
        };

        const [errors, hasErrors] = validateCheckoutDetailsFormData(formData);
        const token = await executeRecaptcha();

        setFormErrors(errors);
        setBotDetected(!token);

        if (hasErrors) {
            scrollIntoViewIncludingNavigation<HTMLFormElement>(formRef);
            return;
        }

        onSubmit(formData);
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`checkout-details-form ${className}`}
        >
            <fieldset className="checkout-details-form__fieldset">
                <legend className="checkout-details-form__legend">
                    {trans('containers.checkoutDetailsForm.personalData')}
                </legend>

                <div className="checkout-details-form__input-row">
                    <TextInput
                        ref={firstNameInputRef}
                        required
                        autoComplete="given-name"
                        autoCapitalize="words"
                        label={trans('form.firstName')}
                        value={firstName}
                        error={formErrors.firstName}
                        disabled={isLoading}
                        onChange={storeFirstName}
                        className="checkout-details-form__input"
                    />

                    <TextInput
                        required
                        autoComplete="family-name"
                        autoCapitalize="words"
                        label={trans('form.lastName')}
                        value={lastName}
                        error={formErrors.lastName}
                        disabled={isLoading}
                        onChange={storeLastName}
                        className="checkout-details-form__input"
                    />
                </div>

                <TextInput
                    required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    label={trans('form.email')}
                    value={email}
                    error={formErrors.email}
                    disabled={isLoading}
                    onChange={storeEmail}
                    className="checkout-details-form__input"
                />

                <TextInput
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    label={trans('form.phoneNumber')}
                    value={phoneNumber}
                    error={formErrors.phoneNumber}
                    disabled={isLoading}
                    onChange={storePhoneNumber}
                    className="checkout-details-form__input"
                />

                {!customer && (
                    <Checkbox
                        label={trans('containers.checkoutDetailsForm.subscribeToNewsletter')}
                        checked={subscribeToNewsletter}
                        disabled={isLoading}
                        onChange={storeSubscribeToNewsletter}
                        className="checkout-details-form__newsletter-subscription-input"
                    />
                )}
            </fieldset>

            <fieldset className="checkout-details-form__fieldset">
                <legend className="checkout-details-form__legend">
                    {trans(`entities.address.type.${AddressType.shipping}`)}
                </legend>

                <div className="checkout-details-form__input-row">
                    <TextInput
                        required
                        autoCapitalize="words"
                        label={trans('form.street')}
                        value={shippingStreet}
                        error={formErrors.shippingStreet}
                        disabled={isLoading}
                        onChange={storeShippingStreet}
                        className="checkout-details-form__input"
                    />

                    <TextInput
                        required
                        type="number"
                        inputMode="numeric"
                        autoCapitalize="words"
                        label={trans('form.houseNumber')}
                        value={shippingHouseNumber}
                        error={formErrors.shippingHouseNumber}
                        disabled={isLoading}
                        onChange={storeShippingHouseNumber}
                        className="checkout-details-form__input"
                    />

                    <TextInput
                        autoCapitalize="words"
                        label={trans('form.addition')}
                        value={shippingAddition}
                        disabled={isLoading}
                        onChange={storeShippingAddition}
                        className="checkout-details-form__input"
                    />
                </div>

                <div className="checkout-details-form__input-row">
                    <TextInput
                        required
                        autoCapitalize="words"
                        label={trans('form.postalCode')}
                        value={shippingPostalCode}
                        error={formErrors.shippingPostalCode}
                        maxLength={7}
                        disabled={isLoading}
                        onChange={storeShippingPostalCode}
                        className="checkout-details-form__input"
                        fieldClassName="checkout-details-form__field--postal-code"
                    />

                    <TextInput
                        required
                        autoCapitalize="words"
                        label={trans('form.city')}
                        value={shippingCity}
                        error={formErrors.shippingCity}
                        disabled={isLoading}
                        onChange={storeShippingCity}
                        className="checkout-details-form__input"
                    />
                </div>

                <Checkbox
                    label={trans('containers.checkoutDetailsForm.hasBillingAddress')}
                    checked={hasBillingAddress}
                    disabled={isLoading}
                    onChange={storeHasBillingAddress}
                    className="checkout-details-form__input"
                />
            </fieldset>

            {hasBillingAddress && (
                <fieldset className="checkout-details-form__fieldset">
                    <legend className="checkout-details-form__legend">
                        {trans(`entities.address.type.${AddressType.billing}`)}
                    </legend>

                    <div className="checkout-details-form__input-row">
                        <TextInput
                            required
                            autoCapitalize="words"
                            label={trans('form.street')}
                            value={billingStreet}
                            error={formErrors.billingStreet}
                            disabled={isLoading}
                            onChange={storeBillingStreet}
                            className="checkout-details-form__input"
                        />

                        <TextInput
                            required
                            type="number"
                            inputMode="numeric"
                            label={trans('form.houseNumber')}
                            value={billingHouseNumber}
                            error={formErrors.billingHouseNumber}
                            disabled={isLoading}
                            onChange={storeBillingHouseNumber}
                            className="checkout-details-form__input"
                        />

                        <TextInput
                            autoCapitalize="words"
                            label={trans('form.addition')}
                            value={billingAddition}
                            disabled={isLoading}
                            onChange={storeBillingAddition}
                            className="checkout-details-form__input"
                        />
                    </div>

                    <div className="checkout-details-form__input-row">
                        <TextInput
                            required
                            autoCapitalize="words"
                            label={trans('form.postalCode')}
                            value={billingPostalCode}
                            error={formErrors.billingPostalCode}
                            maxLength={7}
                            disabled={isLoading}
                            onChange={storeBillingPostalCode}
                            className="checkout-details-form__input"
                            fieldClassName="checkout-details-form__field--postal-code"
                        />

                        <TextInput
                            required
                            autoCapitalize="words"
                            label={trans('form.city')}
                            value={billingCity}
                            error={formErrors.billingCity}
                            disabled={isLoading}
                            onChange={storeBillingCity}
                            className="checkout-details-form__input"
                        />
                    </div>
                </fieldset>
            )}

            {!customer && (
                <fieldset className="checkout-details-form__fieldset">
                    <legend className="checkout-details-form__legend">
                        {trans('containers.checkoutDetailsForm.createAccount')}
                    </legend>

                    <Checkbox
                        label={trans('containers.checkoutDetailsForm.createAccountLabel')}
                        checked={createAccount}
                        disabled={isLoading}
                        onChange={storeCreateAccount}
                        className="checkout-details-form__newsletter-subscription-input"
                    />

                    {createAccount && (
                        <PasswordInput
                            required
                            autoComplete="new-password"
                            label={trans('form.password')}
                            value={password}
                            error={formErrors.password}
                            disabled={isLoading}
                            onChange={setPassword}
                            className="checkout-details-form__input"
                            fieldClassName="checkout-details-form__field--password"
                        />
                    )}
                </fieldset>
            )}

            <p className="checkout-details-form__terms-disclaimer">
                {trans('containers.checkoutDetailsForm.agreeWithTerms')}

                <Link
                    to={trans(appRoutes[AppRoute.customerServiceTerms].path)}
                    target={LinkTarget.blank}
                    className="checkout-details-form__terms-link"
                >
                    {trans('containers.checkoutDetailsForm.terms')}
                </Link>
            </p>

            <IconButton
                hasAnimation
                type="submit"
                icon="arrow-right"
                iconPos={HorizontalAlignment.right}
                text={trans('containers.checkoutDetailsForm.nextStepButton')}
                className="checkout-details-form__submit-button"
            />

            <RecaptchaDisclaimer className="checkout-details-form__recaptcha-disclaimer" />

            {botDetected && (
                <ErrorLabel text={trans('errors.unknownError')} />
            )}
        </form>
    );
};
